if (!customElements.get('design-ideas-gallery')) {
  customElements.define(
    'design-ideas-gallery',
    class DesignIdeasGallery extends HTMLElement {
      connectedCallback() {
        if (this._init) return;
        this._init = true;

        /* ── Config from data attributes ─────────── */
        this.sectionId = this.dataset.sectionId;
        this.storefrontToken = this.dataset.storefrontToken || '';
        this.customizeUrl = this.dataset.customizeUrl || '/pages/customize-design';
        this.ctaText = this.dataset.ctaText || 'Customize This Design';
        this.colsDesktop = parseInt(this.dataset.columnsDesktop) || 6;
        this.colsMobile = parseInt(this.dataset.columnsMobile) || 2;
        this.lockedCategory = this.dataset.lockedCategory || '';

        /* ── DOM references ──────────────────────── */
        this.grid = this.querySelector('[data-design-grid]');
        this.featuredWrap = this.querySelector('[data-featured-wrap]');
        this.featuredGrid = this.querySelector('[data-featured-grid]');
        this.noResultsEl = this.querySelector('[data-no-results]');
        this.statusEl = this.querySelector('[data-filter-status]');
        this.modal = document.getElementById(`DesignPreviewModal-${this.sectionId}`);
        this.searchInput = this.querySelector('[data-design-search]');
        this.categorySelect = this.querySelector('[data-category-filter]');
        this.tagSelect = this.querySelector('[data-tag-filter]');
        this.clearBtns = this.querySelectorAll('[data-clear-filters]');
        this.loadMoreWrap = this.querySelector('[data-load-more]');
        this.progressEl = this.querySelector('[data-load-progress]');
        this.loadMoreBtn = this.querySelector('[data-load-more-btn]');

        /* ── State ───────────────────────────────── */
        this.catalog = [];
        this.filters = { search: '', category: '', tag: '' };
        this.PAGE_SIZE = 50;
        this.displayCount = this.PAGE_SIZE;
        this.totalPages = 1;
        this.totalItems = 0;
        this.featuredLimit = parseInt(this.dataset.featuredLimit, 10) || 24;
        this.featuredCatalog = [];
        this.excludeFeaturedFromMain = false;
        this.featuredQueryFailed = false;
        this.allLoaded = false;
        this.dirty = false;
        this.categories = new Map();
        this.tags = new Map();
        this._preloaded = new Set();
        this._abort = new AbortController();

        this._seenHandles = new Set();
        this._cacheLoaded = false;
        this._silentRefresh = false;

        /* ── Initialize ──────────────────────────── */
        this.loadCachedFilters();
        this.parseServerData();
        this.loadCachedCatalog();
        this.bindEvents();
        this.applyUrlParams();

        /* Featured: from cache (instant) or API (async, non-blocking) */
        if (this._cacheLoaded) {
          this.syncFeaturedFromCatalog();
        } else {
          this.loadFeaturedRail(); /* fire-and-forget, renders when ready */
        }

        /* Main catalog: loads in parallel, main grid is already server-rendered */
        this.loadRemainingPages();
      }

      disconnectedCallback() {
        this._abort.abort();
      }

      /* ────────────────────────────────────────────
         Data Parsing
      ──────────────────────────────────────────── */

      loadCachedFilters() {
        try {
          const raw = localStorage.getItem('design-gallery-filters');
          if (!raw) return;
          const cached = JSON.parse(raw);
          if (cached.categories) {
            for (const [h, n] of cached.categories) this.categories.set(h, n);
          }
          if (cached.tags) {
            for (const [h, n] of cached.tags) this.tags.set(h, n);
          }
          this.updateDropdowns();
        } catch (e) { /* ignore corrupt cache */ }
      }

      saveCachedFilters() {
        if (this.lockedCategory) return; /* locked instances would pollute shared cache */
        try {
          localStorage.setItem('design-gallery-filters', JSON.stringify({
            categories: [...this.categories.entries()],
            tags: [...this.tags.entries()]
          }));
        } catch (e) { /* storage full or unavailable */ }
      }

      loadCachedCatalog() {
        const CACHE_VERSION = 4;
        try {
          const raw = localStorage.getItem('design-gallery-catalog');
          if (!raw) return;
          const cached = JSON.parse(raw);
          if ((cached.version || 0) < CACHE_VERSION) return;
          const age = Date.now() - (cached.timestamp || 0);
          if (age > 4 * 60 * 60 * 1000) return; /* 4 hour TTL */
          if (!cached.designs?.length) return;
          this.addDesigns(cached.designs);
          this.updateDropdowns();
          this._cacheLoaded = true;
        } catch (e) { /* ignore corrupt cache */ }
      }

      saveCatalogCache() {
        if (this.lockedCategory) return; /* locked instances would pollute shared cache */
        const CACHE_VERSION = 4;
        try {
          const designs = this.catalog.map(({ _imageGid, _sortKey, ...d }) => d);
          localStorage.setItem('design-gallery-catalog', JSON.stringify({
            version: CACHE_VERSION,
            timestamp: Date.now(),
            designs,
          }));
        } catch (e) { /* storage full */ }
      }

      parseServerData() {
        const el = this.querySelector('script[data-design-page]');
        if (!el) return;
        try {
          const d = JSON.parse(el.textContent);
          this.totalPages = d.totalPages || 1;
          this.totalItems = d.totalItems || 0;
          this.addDesigns(d.designs || []);
          this.updateDropdowns();
        } catch (e) { /* ignore parse errors */ }
      }

      addDesigns(designs) {
        for (const d of designs) {
          if (this.lockedCategory && d.categoryHandle !== this.lockedCategory) continue;
          /* Dedup by both handle AND title to catch cross-source duplicates */
          if (d.handle && this._seenHandles.has(d.handle)) continue;
          if (d.title && this._seenHandles.has(d.title)) continue;
          if (d.handle) this._seenHandles.add(d.handle);
          if (d.title) this._seenHandles.add(d.title);
          if (d._sortKey == null) d._sortKey = Math.random();
          this.catalog.push(d);
          if (d.categoryHandle) this.categories.set(d.categoryHandle, d.category);
          if (d.tags) {
            for (const t of d.tags) {
              const h = this.handleize(t);
              if (h) this.tags.set(h, t.trim());
            }
          }
        }
        /* Featured first, then shuffled (stable per session via _sortKey) */
        this.catalog.sort((a, b) => {
          if (a.featured !== b.featured) return a.featured ? -1 : 1;
          return a._sortKey - b._sortKey;
        });

        if (this.featuredQueryFailed) this.syncFeaturedFromCatalog();
      }

      /* ────────────────────────────────────────────
         Background Loading via Storefront API
      ──────────────────────────────────────────── */

      async loadRemainingPages() {
        if (!this.storefrontToken || this.totalPages <= 1) {
          this.allLoaded = true;
          this.saveCachedFilters();
          if (this._cacheLoaded) this.saveCatalogCache();
          this.updateLoadUI();
          return;
        }

        /* Cache hit → instant results, silent background refresh */
        if (this._cacheLoaded) {
          this.allLoaded = true;
          this.renderVisible(); /* Re-render immediately with correct sort */
          this.updateLoadUI();
          this._silentRefresh = true;
          try {
            await this.loadViaStorefrontAPI();
          } catch (e) { /* ignore */ }
          this._silentRefresh = false;
          this.saveFeaturedHandles();
          this.saveCatalogCache();
          this.saveCachedFilters();
          this.renderVisible(); /* Pick up any new designs */
          return;
        }

        /* No cache → continue loading remaining catalog pages */
        this.updateLoadUI();

        try {
          await this.loadViaStorefrontAPI();
        } catch (e) {
          if (e.name === 'AbortError') return;
        }

        this.allLoaded = true;
        this.saveFeaturedHandles();
        this.saveCatalogCache();
        this.saveCachedFilters();
        this.updateLoadUI();
        this.renderVisible(); /* Re-render with correct sort */
      }

      async loadFeaturedRail() {
        if (!this.storefrontToken) return;
        if (!this.featuredWrap || !this.featuredGrid) return;
        if (this.featuredCatalog.length) return; /* already populated from cache */

        try {
          let designs = await this.fetchFeaturedByHandles();
          if (!designs) designs = await this.fetchFeaturedByQuery();

          if (this.lockedCategory && designs) {
            designs = designs.filter((d) => d.categoryHandle === this.lockedCategory);
          }

          if (!designs?.length) {
            this.featuredQueryFailed = true;
            this.syncFeaturedFromCatalog();
            return;
          }

          designs.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
          this.featuredCatalog = designs;
          this.excludeFeaturedFromMain = true;
          this.featuredQueryFailed = false;
          this.renderFeatured(); /* show immediately with placeholders */
          this.renderVisible();
          await this.resolveImages(designs, 'featured-rail-images');
          this.renderFeatured(); /* re-render with images */
        } catch (e) {
          this.featuredQueryFailed = true;
          this.syncFeaturedFromCatalog();
        }
      }

      /* Fast path: query specific handles saved from a previous full load */
      async fetchFeaturedByHandles() {
        const handles = this.getSavedFeaturedHandles();
        if (!handles.length) return null;

        const parts = handles.map((h, i) =>
          `d${i}: metaobject(handle: { handle: ${JSON.stringify(h)}, type: "design_idea" }) { handle fields { key value } }`
        );
        const resp = await this.sfFetch(`query { ${parts.join('\n')} }`, {}, 'featured-handles');
        if (!resp) return null;

        const designs = [];
        for (const key of Object.keys(resp)) {
          if (resp[key]) designs.push(this.parseNode(resp[key]));
        }
        return designs.length ? designs : null;
      }

      /* Slow path: use metaobject query filter (may not work for all field types) */
      async fetchFeaturedByQuery() {
        const query = `query ($first: Int!) {
          metaobjects(type: "design_idea", first: $first, query: "featured:true") {
            nodes { handle fields { key value } }
          }
        }`;
        const resp = await this.sfFetch(query, { first: this.featuredLimit }, 'featured-query');
        const nodes = resp?.metaobjects?.nodes || [];
        return nodes.length ? nodes.map((n) => this.parseNode(n)) : null;
      }

      getSavedFeaturedHandles() {
        try {
          const raw = localStorage.getItem('design-gallery-featured');
          if (!raw) return [];
          return JSON.parse(raw).filter(Boolean);
        } catch (e) { return []; }
      }

      saveFeaturedHandles() {
        if (this.lockedCategory) return; /* locked instances would pollute shared cache */
        try {
          const handles = this.catalog
            .filter((d) => d.featured)
            .map((d) => d.handle)
            .filter(Boolean);
          localStorage.setItem('design-gallery-featured', JSON.stringify(handles));
        } catch (e) { /* storage unavailable */ }
      }

      syncFeaturedFromCatalog() {
        const fallback = this.catalog
          .filter((d) => d.featured)
          .slice(0, this.featuredLimit);

        this.featuredCatalog = fallback;
        this.excludeFeaturedFromMain = fallback.length > 0;
        this.renderFeatured();
        this.renderVisible();
      }

      async loadViaStorefrontAPI() {
        const metaQuery = `query ($cursor: String) {
          metaobjects(type: "design_idea", first: 250, after: $cursor) {
            nodes {
              handle
              fields { key value }
            }
            pageInfo { hasNextPage endCursor }
          }
        }`;

        let cursor = null;
        let hasNext = true;

        while (hasNext) {
          /* 1. Load metaobject batch */
          const resp = await this.sfFetch(metaQuery, { cursor }, 'meta');
          if (!resp) break;

          const data = resp.metaobjects;
          if (!data?.nodes?.length) break;

          const designs = data.nodes.map((n) => this.parseNode(n));

          /* 2. Resolve image GIDs in a single batch query */
          await this.resolveImages(designs, 'images');

          this.addDesigns(designs);

          if (!this._silentRefresh) {
            this.updateDropdowns();
            this.updateLoadUI();
            /* Re-render while filtering so results stay accurate during background loading */
            if (this.hasActiveFilters()) {
              this.renderVisible();
            }
          }

          hasNext = data.pageInfo.hasNextPage;
          cursor = data.pageInfo.endCursor;
        }
      }

      async sfFetch(query, variables, label) {
        const resp = await fetch('/api/2024-10/graphql.json', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': this.storefrontToken,
          },
          body: JSON.stringify({ query, variables }),
          signal: this._abort.signal,
        });
        if (!resp.ok) return null;
        const json = await resp.json();
        if (json.errors) return null;
        return json.data;
      }

      async resolveImages(designs, label = 'images') {
        const imageGids = designs.map((d) => d._imageGid).filter(Boolean);
        if (!imageGids.length) return;

        const imageQuery = `query ($ids: [ID!]!) {
          nodes(ids: $ids) {
            ... on MediaImage {
              id
              image { url width height }
            }
          }
        }`;

        const imgResp = await this.sfFetch(imageQuery, { ids: imageGids }, label);
        if (!imgResp?.nodes) return;

        const urlMap = new Map();
        for (const n of imgResp.nodes) {
          if (n?.id && n?.image?.url) urlMap.set(n.id, n.image);
        }

        for (const d of designs) {
          const img = urlMap.get(d._imageGid);
          if (!img) continue;
          const sep = img.url.includes('?') ? '&' : '?';
          d.image = `${img.url}${sep}width=400`;
          d.imageFull = `${img.url}${sep}width=1200`;
          d.imageWidth = img.width || 400;
          d.imageHeight = img.height || 400;
        }
      }

      parseNode(node) {
        const f = {};
        for (const field of node.fields) f[field.key] = field;

        let tags = [];
        if (f.tags?.value) {
          try { tags = JSON.parse(f.tags.value); }
          catch { tags = f.tags.value.split(',').map((t) => t.trim()); }
        }

        const rawCat = (f.category?.value || '').replace(/[\[\]"]/g, '');

        return {
          handle: node.handle,
          _imageGid: f.image?.value || '',
          title: f.title?.value || '',
          category: rawCat,
          categoryHandle: this.handleize(rawCat),
          tags,
          description: f.description?.value || '',
          image: '',
          imageFull: '',
          imageWidth: 400,
          imageHeight: 400,
          featured: f.featured?.value === 'true' || f.featured?.value === true,
        };
      }

      /* ────────────────────────────────────────────
         Filtering
      ──────────────────────────────────────────── */

      getFiltered() {
        const baseCatalog = this.excludeFeaturedFromMain
          ? this.catalog.filter((d) => !d.featured)
          : this.catalog;
        if (!this.hasActiveFilters()) return baseCatalog;
        return baseCatalog
          .filter((d) => this.matchSearch(d) && this.matchCategory(d) && this.matchTag(d))
          .sort((a, b) => {
            if (a.featured !== b.featured) return a.featured ? -1 : 1;
            return (a.title || '').localeCompare(b.title || '');
          });
      }

      matchSearch(d) {
        if (!this.filters.search) return true;
        const hay =
          `${d.title} ${d.description || ''} ${d.category} ${(d.tags || []).join(' ')}`.toLowerCase();
        return hay.includes(this.filters.search);
      }

      matchCategory(d) {
        if (!this.filters.category) return true;
        return d.categoryHandle === this.filters.category;
      }

      matchTag(d) {
        if (!this.filters.tag) return true;
        return (d.tags || []).some((t) => this.handleize(t) === this.filters.tag);
      }

      hasActiveFilters() {
        return !!(this.filters.search || this.filters.category || this.filters.tag);
      }

      applyFilters() {
        this.displayCount = this.PAGE_SIZE;
        this.renderVisible();
        this.syncUrl();
      }

      clearFilters() {
        this.filters = { search: '', category: '', tag: '' };
        if (this.searchInput) this.searchInput.value = '';
        if (this.categorySelect) this.categorySelect.value = '';
        if (this.tagSelect) this.tagSelect.value = '';
        this.displayCount = this.PAGE_SIZE;
        this.renderVisible();
        this.syncUrl();
      }

      syncUrl() {
        const params = new URLSearchParams();
        if (this.filters.category) params.set('category', this.filters.category);
        if (this.filters.tag) params.set('tag', this.filters.tag);
        if (this.filters.search) params.set('search', this.filters.search);
        const qs = params.toString();
        const url = window.location.pathname + (qs ? `?${qs}` : '');
        history.replaceState(null, '', url);
      }

      /* ────────────────────────────────────────────
         Rendering
      ──────────────────────────────────────────── */

      renderVisible() {
        this.dirty = true;
        const filtered = this.getFiltered();
        const visible = filtered.slice(0, this.displayCount);

        this.grid.innerHTML = visible
          .map((d, i) => this.cardHTML(d, i >= this.colsDesktop))
          .join('');

        this.grid.style.display = visible.length ? '' : 'none';
        this.updateStatus(visible.length, filtered.length);
        this.updateShowMore(filtered.length);
        this.updateClearBtn();
        this.updateNoResults(visible.length);
      }

      renderFeatured() {
        if (!this.featuredWrap || !this.featuredGrid) return;
        if (!this.featuredCatalog.length) {
          this.featuredWrap.hidden = true;
          this.featuredGrid.innerHTML = '';
          return;
        }

        this.featuredWrap.hidden = false;
        this.featuredGrid.innerHTML = this.featuredCatalog
          .map((d, i) => this.cardHTML(d, i >= this.colsDesktop))
          .join('');
      }

      showMore() {
        const oldCount = this.displayCount;
        this.displayCount += this.PAGE_SIZE;
        const filtered = this.getFiltered();
        const newOnes = filtered.slice(oldCount, this.displayCount);

        if (newOnes.length) {
          const frag = document.createDocumentFragment();
          const tmp = document.createElement('div');
          for (const d of newOnes) {
            tmp.innerHTML = this.cardHTML(d, true, true);
            frag.appendChild(tmp.firstElementChild);
          }
          this.grid.appendChild(frag);
        }

        this.dirty = true;
        const shown = Math.min(this.displayCount, filtered.length);
        this.updateStatus(shown, filtered.length);
        this.updateShowMore(filtered.length);
      }

      cardHTML(design, lazy = true, entering = false) {
        const img = design.image || '';
        const full = design.imageFull || img;
        const title = this.esc(design.title || '');
        const cat = this.esc(design.category || '');
        const catH = design.categoryHandle || '';
        const desc = this.esc(design.description || '');
        const tagsH = (design.tags || [])
          .map((t) => this.handleize(t))
          .filter(Boolean)
          .join(',');
        const sizes = `(max-width: 749px) calc(100vw / ${this.colsMobile}), calc(100vw / ${this.colsDesktop})`;
        const srcset = img
          ? [200, 300, 400, 600].map((w) => `${this.imgW(img, w)} ${w}w`).join(', ')
          : '';

        return `<div class="design-card${entering ? ' design-card--entering' : ''}" data-design-card data-category="${catH}" data-tags="${tagsH}" data-title="${(design.title || '').toLowerCase()}" data-description="${(design.description || '').toLowerCase()}" role="button" tabindex="0" aria-label="Preview ${title}" data-preview-btn data-design-title="${title}" data-design-image="${this.esc(full)}" data-design-category="${cat}" data-design-description="${desc}">
  <div class="design-card__image-wrapper">
    ${
      img
        ? `<img src="${this.esc(img)}" srcset="${srcset}" sizes="${sizes}" alt="${title}" class="design-card__image" loading="${lazy ? 'lazy' : 'eager'}" fetchpriority="${lazy ? 'auto' : 'high'}" width="${design.imageWidth || 400}" height="${design.imageHeight || 400}">`
        : '<div class="design-card__placeholder"></div>'
    }
    <div class="design-card__overlay"><span class="design-card__preview-btn button button--secondary">Quick View</span></div>
  </div>
  <div class="design-card__info">
    <h3 class="design-card__title h4">${title}</h3>
    ${cat ? `<span class="design-card__category">${cat}</span>` : ''}
  </div>
</div>`;
      }

      /* ────────────────────────────────────────────
         UI Updates
      ──────────────────────────────────────────── */

      updateDropdowns() {
        if (this.categorySelect) {
          const cur = this.categorySelect.value;
          const activeCategories = new Map();
          for (const d of this.catalog) {
            if (d.categoryHandle) activeCategories.set(d.categoryHandle, d.category);
          }
          const opts = [...activeCategories.entries()].sort((a, b) =>
            a[1].localeCompare(b[1])
          );
          this.categorySelect.innerHTML =
            '<option value="">All Categories</option>' +
            opts
              .map(
                ([h, n]) =>
                  `<option value="${h}"${h === cur ? ' selected' : ''}>${this.esc(n)}</option>`
              )
              .join('');
        }

        if (this.tagSelect) {
          const cur = this.tagSelect.value;
          const activeTags = new Map();
          for (const d of this.catalog) {
            if (d.tags) {
              for (const t of d.tags) {
                const h = this.handleize(t);
                if (h) activeTags.set(h, t.trim());
              }
            }
          }
          const opts = [...activeTags.entries()].sort((a, b) =>
            a[1].localeCompare(b[1])
          );
          this.tagSelect.innerHTML =
            '<option value="">All Tags</option>' +
            opts
              .map(
                ([h, n]) =>
                  `<option value="${h}"${h === cur ? ' selected' : ''}>${this.esc(n)}</option>`
              )
              .join('');
        }
      }

      updateLoadUI() {
        if (!this.loadMoreWrap) return;

        if (this.allLoaded) {
          if (this.progressEl) {
            this.progressEl.textContent = `${this.catalog.length.toLocaleString()} designs loaded`;
            setTimeout(
              () => this.progressEl.classList.add('design-load-more__progress--done'),
              2000
            );
          }
          this.updateShowMore(this.getFiltered().length);
        } else {
          if (this.progressEl) {
            this.progressEl.textContent = `Loading designs\u2026 ${this.catalog.length.toLocaleString()} / ${this.totalItems.toLocaleString()}`;
            this.progressEl.classList.remove('design-load-more__progress--done');
          }
          this.loadMoreWrap.style.display = '';
        }
      }

      updateShowMore(totalFiltered) {
        if (!this.loadMoreBtn || !this.loadMoreWrap) return;
        const remaining = totalFiltered - this.displayCount;

        if (remaining > 0) {
          this.loadMoreBtn.textContent = `Show More Designs (${remaining.toLocaleString()} remaining)`;
          this.loadMoreBtn.style.display = '';
          this.loadMoreWrap.style.display = '';
        } else {
          this.loadMoreBtn.style.display = 'none';
          if (
            this.allLoaded &&
            this.progressEl?.classList.contains('design-load-more__progress--done')
          ) {
            this.loadMoreWrap.style.display = 'none';
          }
        }
      }

      updateStatus(shown, total) {
        if (!this.statusEl) return;
        if (this.hasActiveFilters()) {
          const suffix = this.allLoaded ? '' : ' (still loading\u2026)';
          this.statusEl.textContent = `Showing ${shown.toLocaleString()} of ${total.toLocaleString()} designs${suffix}`;
        } else {
          this.statusEl.textContent = '';
        }
      }

      updateClearBtn() {
        const show = this.hasActiveFilters();
        this.clearBtns.forEach((b) => (b.style.display = show ? '' : 'none'));
      }

      updateNoResults(count) {
        if (!this.noResultsEl) return;
        this.noResultsEl.style.display =
          count === 0 && this.hasActiveFilters() ? '' : 'none';
      }

      /* ────────────────────────────────────────────
         Events
      ──────────────────────────────────────────── */

      bindEvents() {
        /* Filters */
        if (this.searchInput) {
          this.searchInput.addEventListener(
            'input',
            this.debounce(() => {
              this.filters.search = this.searchInput.value.toLowerCase().trim();
              this.applyFilters();
            }, 300)
          );
        }

        if (this.categorySelect) {
          this.categorySelect.addEventListener('change', () => {
            this.filters.category = this.categorySelect.value;
            this.applyFilters();
          });
        }

        if (this.tagSelect) {
          this.tagSelect.addEventListener('change', () => {
            this.filters.tag = this.tagSelect.value;
            this.applyFilters();
          });
        }

        this.clearBtns.forEach((b) =>
          b.addEventListener('click', () => this.clearFilters())
        );

        /* Load More */
        if (this.loadMoreBtn) {
          this.loadMoreBtn.addEventListener('click', () => this.showMore());
        }

        this.bindPreviewEvents(this.grid);
        this.bindPreviewEvents(this.featuredGrid);

        /* Modal */
        if (this.modal) {
          this.modal.addEventListener('click', (e) => {
            if (e.target === this.modal) this.modal.hide();
          });

          document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.modal.hasAttribute('open')) {
              this.modal.hide();
            }
          });

          const wrap = this.modal.querySelector('[data-modal-image]')?.parentElement;
          if (wrap) {
            wrap.addEventListener('click', () =>
              wrap.classList.toggle('design-modal__image-wrapper--zoomed')
            );

            wrap.addEventListener('mousemove', (e) => {
              if (!wrap.classList.contains('design-modal__image-wrapper--zoomed')) return;
              const r = wrap.getBoundingClientRect();
              const x = ((e.clientX - r.left) / r.width) * 100;
              const y = ((e.clientY - r.top) / r.height) * 100;
              const img = wrap.querySelector('[data-modal-image]');
              if (img) img.style.transformOrigin = `${x}% ${y}%`;
            });

            wrap.addEventListener('mouseleave', () => {
              wrap.classList.remove('design-modal__image-wrapper--zoomed');
              const img = wrap.querySelector('[data-modal-image]');
              if (img) img.style.transformOrigin = 'center center';
            });
          }
        }
      }

      bindPreviewEvents(container) {
        if (!container) return;

        /* Delegated events support both server-rendered and JS-rendered cards */
        container.addEventListener('click', (e) => {
          const btn = e.target.closest('[data-preview-btn]');
          if (!btn) return;
          e.preventDefault();
          this.openPreview(btn);
        });

        container.addEventListener('keydown', (e) => {
          if (e.key !== 'Enter' && e.key !== ' ') return;
          const btn = e.target.closest('[data-preview-btn]');
          if (!btn) return;
          e.preventDefault();
          this.openPreview(btn);
        });

        container.addEventListener('mouseover', (e) => {
          const btn = e.target.closest('[data-preview-btn]');
          if (!btn) return;
          const src = btn.dataset.designImage;
          if (src && !this._preloaded.has(src)) {
            this._preloaded.add(src);
            new Image().src = src;
          }
        });
      }

      /* ────────────────────────────────────────────
         Preview Modal
      ──────────────────────────────────────────── */

      openPreview(btn) {
        if (!this.modal) return;

        const title = btn.dataset.designTitle || '';
        const full = btn.dataset.designImage || '';
        const category = btn.dataset.designCategory || '';
        const desc = btn.dataset.designDescription || '';
        const cardImg = btn.querySelector('.design-card__image');
        const thumb = cardImg ? cardImg.currentSrc || cardImg.src : '';

        const mImg = this.modal.querySelector('[data-modal-image]');
        const mTitle = this.modal.querySelector('[data-modal-title]');
        const mCat = this.modal.querySelector('[data-modal-category]');
        const mDesc = this.modal.querySelector('[data-modal-description]');
        const mCta = this.modal.querySelector('[data-modal-cta]');

        const wrap = mImg?.parentElement;
        if (wrap) wrap.classList.remove('design-modal__image-wrapper--zoomed');

        if (mImg) {
          mImg.classList.remove('design-modal__image--loaded');
          mImg.src = thumb || '';
          mImg.alt = title;
        }
        if (mTitle) mTitle.textContent = title;
        if (mCat) {
          mCat.textContent = category;
          mCat.style.display = category ? 'inline-block' : 'none';
        }
        if (mDesc) mDesc.textContent = desc;

        this.modal.show(btn);

        if (mImg && full && full !== thumb) {
          const hi = new Image();
          hi.onload = () => {
            mImg.src = full;
            mImg.classList.add('design-modal__image--loaded');
          };
          hi.src = full;
        } else if (mImg) {
          mImg.classList.add('design-modal__image--loaded');
        }

        if (mCta) {
          const base = mCta.href.split('?')[0];
          mCta.href = `${base}?design=${encodeURIComponent(title)}&image=${encodeURIComponent(full)}`;
        }
      }

      /* ────────────────────────────────────────────
         URL Params (deep linking)
      ──────────────────────────────────────────── */

      applyUrlParams() {
        const p = new URLSearchParams(window.location.search);
        const cat = p.get('category');
        const tag = p.get('tag');
        const search = p.get('search');

        if (cat) {
          this.filters.category = cat;
          if (this.categorySelect) {
            const opt = [...this.categorySelect.options].find((o) => o.value === cat);
            if (opt) this.categorySelect.value = cat;
          }
        }

        if (tag) {
          this.filters.tag = tag;
          if (this.tagSelect) {
            const opt = [...this.tagSelect.options].find((o) => o.value === tag);
            if (opt) this.tagSelect.value = tag;
          }
        }

        if (search) {
          this.filters.search = search.toLowerCase().trim();
          if (this.searchInput) this.searchInput.value = search;
        }

        if (this.hasActiveFilters()) this.applyFilters();
      }

      /* ────────────────────────────────────────────
         Utilities
      ──────────────────────────────────────────── */

      handleize(str) {
        return (str || '')
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-|-$/g, '');
      }

      esc(s) {
        return (s || '')
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#39;');
      }

      imgW(url, w) {
        return url.replace(/width=\d+/, `width=${w}`);
      }

      debounce(fn, ms) {
        let t;
        return (...a) => {
          clearTimeout(t);
          t = setTimeout(() => fn.apply(this, a), ms);
        };
      }

    }
  );
}
